import { createElement, lazy, useState, type ComponentType } from 'react';

/** A route's dynamic import that remembers the page component once any caller has loaded it. */
export interface RouteLoader {
  (): Promise<{ default: ComponentType }>;
  loaded?: ComponentType;
}

export function routeLoader(load: () => Promise<{ default: ComponentType }>): RouteLoader {
  const loader: RouteLoader = () =>
    load().then((module) => {
      loader.loaded = module.default;
      return module;
    });
  return loader;
}

/**
 * `React.lazy` for a route that renders synchronously once its code is loaded.
 *
 * Plain `lazy` suspends on its first render even when the module is already in
 * memory (it only learns the import has settled by awaiting it). The router runs
 * navigations outside a transition (`main.tsx`), so that one suspension would
 * flash the loading state on every first visit to a page the hover or idle
 * preload has already fetched.
 */
export function lazyRoute(loader: RouteLoader): ComponentType {
  const Lazy = lazy(loader);
  return function LazyRoute() {
    // Chosen once per mount: swapping from Lazy to the loaded component after the
    // import settles would change the element type and remount the page.
    const [page] = useState(() => createElement(loader.loaded ?? Lazy));
    return page;
  };
}
